import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useAdminDashboardPieChart } from './AdminDashboardPieChart.hook';
import { chartTooltip, chartWrapper, loaderWrapper } from './AdminDashboardPieChart.style';
import { AdminDashboardPieChartDataPropsType } from './AdminDashboardPieChart.types';
import Box from 'yoda-ui/components/Box';
import Loader from 'yoda-ui/components/Loader';

const AdminDashboardPieChart: FC<AdminDashboardPieChartDataPropsType> = ({
  pieData,
  loading = false,
  isProductGrouping = false,
}) => {
  const { t } = useTranslation();
  const { processedData, colors, renderLabel, customTooltip } = useAdminDashboardPieChart(pieData, isProductGrouping);

  return (
    <Box sx={ chartWrapper }>
      {
        loading
          ? <Box sx={ loaderWrapper }><Loader center/></Box>
          : pieData.length === 0
            ? <Box sx={ loaderWrapper }>{ t('maya_admin_dashboard_charts_no_data_title') }</Box>
            : <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={ processedData }
                  cx="50%"
                  cy="50%"
                  labelLine
                  label={
                    (props) => {
                      const labelData = renderLabel(props);
                      if (!labelData) return null;

                      return (
                        <text
                          x={ labelData?.x }
                          y={ labelData?.y }
                          fill="black"
                          fontSize={ 12 }
                          textAnchor={ labelData?.x > labelData?.cx ? 'start' : 'end' }
                          dominantBaseline="central"
                        >
                          <tspan x={ labelData?.x } dy="0.6em">{ labelData?.categoryName },</tspan>
                          <tspan x={ labelData?.x } dy="1.2em">{
                            `${labelData?.count}, ${(Number(labelData
                              ?.percent) * 100).toFixed(0)}%`
                          }</tspan>
                        </text>
                      );
                    }
                  }
                  outerRadius={ 150 }
                  dataKey="count"
                  nameKey="categoryName"
                  animationBegin={ 0 }
                >
                  {
                    pieData.map((_, index) => (
                      <Cell key={ `cell-${index}` } fill={ colors[index] } />
                    ))
                  }
                </Pie>
                <Tooltip content={
                  (props) => {
                    const customTooltipData = customTooltip(props);
                    if (!customTooltipData) return null;

                    return (
                      <Box sx={ chartTooltip }>
                        <Box>{ customTooltipData.categoryName }: { customTooltipData.count }, { customTooltipData.percent }%</Box>
                        {
                          customTooltipData.children && (
                            <>
                              <hr />
                              {
                                customTooltipData.children.map((item, index) => (
                                  <Box key={ index }>{ item.categoryName }: { item.count }, { item.percent }%</Box>
                                ))
                              }
                            </>
                          )
                        }
                      </Box>
                    );
                  }
                } />
              </PieChart>
            </ResponsiveContainer>
      }
    </Box>
  );
};

export default AdminDashboardPieChart;
